import ky from 'ky'
import { httpError } from '../utils/errors'
import e164ify from "../utils/e164";

/**
 * AccountService provides account API services
 */
class AccountService {

    /**
     * get retrieves the account from the server
     *
     * @param id
     * @returns {Promise<unknown>}
     */
    get = (id) => {
        //console.log('AccountService.get() id=', id)
        return new Promise((resolve, reject) => {
            // Get the account data, transforming any errors into a standardised error object
            ky.get('/api/v2/accounts/'+id).json()
                .then(result => resolve(result))
                .catch(error => httpError(error).then(er => reject(er)))
        })
    }

    /**
     * patch applies a partial update to an account
     *
     * @param accountId
     * @param patch
     * @returns {Promise<unknown>}
     */
    patch = (accountId, patch) => {
        //console.log('AccountService.patch() accountId:', accountId, 'patch=', patch)
        return new Promise((resolve, reject) => {
            const patchSet = {object: 'account', id: accountId, properties: patch};
            const url = `/api/v2/accounts/${accountId}`
            ky.patch(url, {json: patchSet})
                .then(result => resolve(result))
                .catch(error => httpError(error).then(er => reject(er)))
        })
    }

    /**
     * close deactivates an account pending finalisation
     * PhoneThru accounts are finalised immediately
     *
     * @param accountId
     * @param reason
     * @returns {Promise<unknown>}
     */
    close = (accountId, reason) => {
        //console.log('AccountService.close() accountId:', accountId, 'reason=', reason)
        return new Promise((resolve, reject) => {
            const data = {reason};
            const url = `/api/v2/accounts/${accountId}`
            ky.delete(url, {json: data})
                .then(result => resolve(result))
                .catch(error => httpError(error).then(er => reject(er)))
        })
    }

    /**
     * removePaymentMethod removes the default payment method (card) for the account
     *
     * @param accountId
     * @param paymentId
     * @returns {Promise<unknown>}
     */
    removePaymentMethod = (accountId, paymentId) => {
        //console.log('AccountService.removePaymentMethod() accountId:', accountId, 'paymentId', paymentId)
        return new Promise((resolve, reject) => {
            const url = `/api/v2/accounts/${accountId}/payment-method`
            ky.delete(url)
                .then(result => resolve(result))
                .catch(error => httpError(error).then(er => reject(er)))
        })
    }

    /**
     * setPaymentMethod sets the default payment method (card) for the account
     * @param accountId
     * @param tokenId
     * @returns {Promise<unknown>}
     */
    setPaymentMethod = (accountId, tokenId) => {
        //console.log('AccountService.setPaymentMethod() accountId:', accountId, 'token', tokenId)
        return new Promise((resolve, reject) => {
            const url = `/api/v2/accounts/${accountId}/payment-method`
            const data = {token: tokenId};
            ky.put(url, {json: data}).json()
                .then(result => resolve(result))
                .catch(error => httpError(error).then(er => reject(er)))
        })
    }

    /**
     * patchService applies a partial update to an account's service
     *
     * @param accountId
     * @param serviceId
     * @param patch
     * @returns {Promise<unknown>}
     */
    patchService = (accountId, serviceId, patch) => {
        //console.log('AccountService.patchService() accountId:', accountId, 'serviceId', serviceId, 'patch=', patch)
        return new Promise((resolve, reject) => {
            const patchSet = {object: 'service', id: serviceId, properties: patch};
            const url = `/api/v2/accounts/${accountId}/services/${serviceId}`
            ky.patch(url, {json: patchSet})
                .then(result => resolve(result))
                .catch(error => httpError(error).then(er => reject(er)))
        })
    }

    setServiceAction = (accountId, serviceId, action) => {
        //console.log('AccountService.setServiceAction() accountId:', accountId, 'serviceId', serviceId, 'action=', action)
        return new Promise((resolve, reject) => {
            const url = `/api/v2/accounts/${accountId}/services/${serviceId}/action`
            ky.post(url, {json: action})
                .then(result => resolve(result))
                .catch(error => httpError(error).then(er => reject(er)))
        })
    }

    transferService = (accountId, serviceId, rxMobileNumber, rxPassword) => {
        //console.log('AccountService.transferService() accountId:', accountId, 'serviceId', serviceId, 'rxMobileNumber', rxMobileNumber, 'rxPassword', rxPassword)
        return new Promise((resolve, reject) => {
            const url = `/api/v2/accounts/${accountId}/services/${serviceId}/transfer`
            const data = {
                accountId,
                serviceId,
                username: e164ify(rxMobileNumber).substr(1) + '@phonethru.com',
                password: rxPassword
            }
            ky.post(url, {json: data}).json()
                .then(result => resolve(result))
                .catch(error => httpError(error).then(er => reject(er)))
        })
    }

    getServices = (accountId) => {
        //console.log('AccountService.getServices() accountId=', accountId)
        return new Promise((resolve, reject) => {
            // Get the account services data, transforming any errors into a standardised error object
            ky.get(`/api/v2/accounts/${accountId}/services`).json()
                .then(result => resolve(result))
                .catch(error => httpError(error).then(er => reject(er)))
        })
    }

    reactivationQuery = (accountId) => {
        //console.log('AccountService.reactivationQuery() accountId=', accountId)
        return new Promise((resolve, reject) => {
            // Get the account services data, transforming any errors into a standardised error object
            ky.get(`/api/v2/accounts/${accountId}/reactivate`).json()
                .then(result => resolve(result))
                .catch(error => httpError(error).then(er => reject(er)))
        })
    }

    reactivate = (accountId) => {
        //console.log('AccountService.reactivate() accountId=', accountId)
        return new Promise((resolve, reject) => {
            // Post a reactivation request.
            const url = `/api/v2/accounts/${accountId}/reactivate`;
            const data = { numbers: [] }; // don't want to auto-activate any numbers
            ky.post(url, {json: data}).json()
                .then(result => resolve(result))
                .catch(error => httpError(error).then(er => reject(er)))
        })
    }

}

// Create a singleton instance of the service to export
const instance = new AccountService();

export default instance;